import { Add, Clear, Launch, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import { useAppDispatch, useAppSelector} from "../../store/hooks";
import {
  fetchProductsDropDown,
  getJDOpsData,
} from "../../slices/productcostseeds.slice";
import { RootState } from "../../store";
import {
  CompositeFilterDescriptor,
  DataResult,
  SortDescriptor,
  process,
} from "@progress/kendo-data-query";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ProductMappingModal } from "./ProductMappingModal";
import React from "react";
import styled from "styled-components";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const StyledGrid = styled(Grid)`
  .k-column-title {
    min-width: 0;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    font-weight: bold; /* Make the font bold */
    font-family: "Roboto";
  }
  ,
  th {
    border-inline-start-width: 0px !important;
  }
  th > .k-cell-inner > .k-link {
    border-right: 2px solid var(--kendo-color-border);
    justify-content: center;
  }
  ,
  th > .k-cell-inner,
  .k-grid-header-wrap {
    background-color: white;
    height: 55px;
  }
  ,
  .k-table-row,
  .k-table-alt-row,
  .k-grid-table-wrap {
    background-color: #f3f4f6 !important;
  }
  ,
  .k-table-td {
    border-bottom: 1px solid var(--kendo-color-border);
    border-left: none;
    border-right: none;
    font-family: "Roboto";
    text-align: center;
  }
`;
export const JDOPSProductMapping = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [sort] = useState<SortDescriptor[]>([]);
  const exportRef = useRef<ExcelExport | null>(null);
  const [checked, setChecked] = React.useState(false);
  const [page, setPage] = useState({ skip: 0, take: 20 });
  const [jdOpsSearchQuery, setJdOpsSearchQuery] = useState<string>("");
  const { jdOpsProducts } = useAppSelector(
    (state: RootState) => state.seedsdropdown,
  );

  const [filter] = useState<CompositeFilterDescriptor>(initialFilters);
  const gridColumns = [
    {
      field: "jdOpsProductName",
      title: "JD OPS Product Name",
      width: "250px",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "productName",
      title: "Product Name",
      width: "250px",
      headerClassName: "super-app-theme--header",
    },
    {
      title: "Actions",
      width: "100px",
      headerClassName: "super-app-theme--header",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={onEditClick} />
        ),
      },
    },
  ];

  const onEditClick = (dataItem: any) => {
    setIsEdit(true);
    setSelectedProduct(dataItem);
    setOpen(true);
  };

  const handleClearSearch = () => {
    setJdOpsSearchQuery("");
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const handleJdOpsSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setJdOpsSearchQuery(event.target.value);
  };

  const handleJdOpsSearchKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      setJdOpsSearchQuery((event.target as HTMLInputElement).value);
    }
  };

  const handleToBeAssignedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setChecked(event.target.checked);
  };

  const filteredData = jdOpsProducts.filter((item) => {
    //const matchesProductSearch = item.name.toLowerCase().includes(productSearchQuery.toLowerCase());
    const matchesJdOpsSearch = item.jdOpsProductName
      .toLowerCase()
      .includes(jdOpsSearchQuery && jdOpsSearchQuery.toLowerCase());
    const matchesToBeAssigned = checked
      ? item.productName.includes("To be assigned")
      : true;
    return matchesJdOpsSearch && matchesToBeAssigned;
  });

  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(filteredData, {
        sort: sort,
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  useEffect(() => {
    setPage((prev) => ({ ...prev, skip: 0 }));
  }, [jdOpsSearchQuery, checked]);

  useEffect(() => {
    dispatch(getJDOpsData({}));
    dispatch(fetchProductsDropDown({}));
  }, []);

  const handleClose = () => {
    dispatch(getJDOpsData({}));
    setOpen(false);
  };

  const onAddClick = () => {
    setIsEdit(false);
    setSelectedProduct(null);
    setOpen(true);
  }

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <TextField
          placeholder="Search Product"
          id="outlined-basic"
          variant="outlined"
          size="small"
          value={jdOpsSearchQuery}
          onChange={handleJdOpsSearchChange}
          onKeyPress={handleJdOpsSearchKeyPress}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleClearSearch}
                style={jdOpsSearchQuery ? {} : { visibility: "hidden" }}
              >
                <Clear />
              </IconButton>
            ),
            startAdornment: <Search />,
          }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={onAddClick}>
          Add Product Mapping
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={handleToBeAssignedChange}
              color="primary"
            />
          }
          label="To be assigned"
        />
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
        >
          Export
        </Button>
      </Box>
      <Box>
        <ExcelExport data={filteredData} ref={exportRef}>
          <StyledGrid
            data={filteredData}
            dataItemKey="id"
            filter={filter}
            sort={sort}
            scrollable="scrollable"
            autoProcessData={true}
            pageable={{ buttonCount: 5, pageSizes: [10, 20, 30], info: true }}
            total={filteredData.length}
            skip={page.skip}
            take={page.take}
            onPageChange={handlePageChange}
          >
            {gridColumns.map((columnProps: any) => (
              <GridColumn
                key={columnProps.field || columnProps.title}
                {...columnProps}
                title={columnProps.title}
              />
            ))}
          </StyledGrid>
        </ExcelExport>
      </Box>
      {open && (
        <ProductMappingModal
          isEdit={isEdit}
          open={open}
          handleClose={handleClose}
          product={selectedProduct}
        />
      )}
    </Box>
  );
};


